<template>
  <span
    ref="container"
    data-locator="document-search-path"
    class="text-truncate d-inline-flex w-100"
  >
    <AppTooltip
      :max-width="TOOLTIP_MAX_WIDTH"
      location="bottom"
    >
      <template #activator="{ props }">
        <span
          class="text-truncate d-inline-block"
          :class="{ 'room-name-width-limit': file?.fullPath?.length > 0 }"
          v-bind="props"
        >
          {{ file.roomName }}
        </span>
      </template>
      {{ fullPathText }}
    </AppTooltip>
    <template v-if="visibleFolders.length">
      <template v-for="folder in visibleFolders" :key="folder.id">
        <AppTooltip
          :max-width="TOOLTIP_MAX_WIDTH"
          location="bottom"
        >
          <template #activator="{ props }">
            <span v-bind="props">
              <span class="mx-2">></span>
              <span
                class="file-path-part"
                @click="mobile ? null : onClickInPath($event, folder.id, false)"
              >
                {{ folder.name }}
              </span>
            </span>
          </template>
          {{ fullPathText }}
        </AppTooltip>
      </template>
    </template>
    <template v-if="shouldShowEllipsis">
      <AppTooltip
        :max-width="TOOLTIP_MAX_WIDTH"
        location="bottom"
      >
        <template #activator="{ props }">
          <span v-bind="props">
            <span class="mx-2">></span>
            <span>...</span>
          </span>
        </template>
        {{ fullPathText }}
      </AppTooltip>
    </template>
    <template v-if="lastFolder">
      <AppTooltip
        :max-width="TOOLTIP_MAX_WIDTH"
        location="bottom"
      >
        <template #activator="{ props }">
          <span class="d-flex" v-bind="props">
            <span class="mx-2">></span>
            <span
              class="file-path-part text-truncate d-inline-block"
              @click="mobile ? null : onClickInPath($event, lastFolder.id, true)"
            >
              {{ lastFolder.name }}
            </span>
          </span>
        </template>
        {{ fullPathText }}
      </AppTooltip>
    </template>

    <!-- Hidden measurement row -->
    <span
      ref="measureRow"
      style="position: absolute; left: -9999px; top: -9999px; visibility: hidden; white-space: nowrap;"
    >
      <span
        class="text-truncate d-inline-block"
        :class="{ 'room-name-width-limit': file?.fullPath?.length > 0 }"
      >
        {{ file.roomName }}
      </span>
      <template v-for="folder in file.fullPath?.slice(0, -1) || []" :key="folder.id">
        <span class="mx-2">></span>
        <span
          class="file-path-part"
          :ref="el => setMeasureFolderRef((el as HTMLElement))"
        >
          {{ folder.name }}
        </span>
      </template>
      <span class="mx-2">></span>
      <span
        v-if="lastFolder"
        class="file-path-part text-truncate d-inline-block"
        ref="measureLastFolderRef"
      >
        {{ lastFolder.name }}
      </span>
    </span>
  </span>
</template>

<script setup lang="ts">
import { debounce } from 'lodash-es'
import { ref, watch, nextTick, onMounted, onBeforeUnmount, computed } from 'vue'
import { useDisplay } from 'vuetify'

import { openDocumentsFolder } from '@/common/utils/files'

import { FileSearchResult } from './AppBarDocumentSearchResultsItem.vue'
import AppTooltip from '../AppTooltip.vue'

const props = defineProps<{
  file: FileSearchResult
}>()

const emit = defineEmits(['click:result'])

function onClickInPath(event: Event, folderId: number | string, isLastFolderInPath: boolean) {
  event.stopPropagation()
  openDocumentsFolder(folderId, props.file.mnemo, props.file.roomTypeType, isLastFolderInPath ? props.file.id : undefined)
  emit('click:result')
}

const container = ref<HTMLElement | null>(null)
const measureRow = ref<HTMLElement | null>(null)
const measureFolderRefs = ref<HTMLElement[]>([])
const measureLastFolderRef = ref<HTMLElement | null>(null)
const visibleFolders = ref<Array<{ id: number | string, name: string }>>([])
const shouldShowEllipsis = ref(false)

const { mobile } = useDisplay()

const lastFolder = computed(() => {
  const path = props.file.fullPath
  return path && path.length ? path[path.length - 1] : null
})

const fullPathText = computed(() => {
  if (!props.file.fullPath || props.file.fullPath.length === 0) return props.file.roomName
  return `${props.file.roomName} > ${props.file.fullPath.map(f => f.name).join(' > ')}`
})

const CALCULATE_VISIBLE_FOLDERS_DEBOUNCE_MS = 50
const TOOLTIP_MAX_WIDTH = 600

function setMeasureFolderRef(el: HTMLElement | null) {
  measureFolderRefs.value.push(el as HTMLElement)
}

function calculateVisibleFolders() {
  nextTick(() => {
    if (!container.value || !measureRow.value || !props.file.fullPath || props.file.fullPath.length === 0) {
      visibleFolders.value = []
      shouldShowEllipsis.value = false
      return
    }
    const containerWidth = container.value.offsetWidth
    const roomWidth = ((measureRow.value.children[0]) as HTMLElement)?.offsetWidth || 0
    const arrowWidth = 24 // 24px for '>' and the mx-2 margin space
    const folders = props.file.fullPath.slice(0, -1)
    let totalWidth = roomWidth
    let visible = []
    let i = 0
    for (; i < folders.length; i++) {
      const el = measureFolderRefs.value[i]
      if (!el) break
      // Predict width if we add this folder, plus the arrow
      let nextWidth = totalWidth + arrowWidth + el.offsetWidth
      if (nextWidth > containerWidth) break
      totalWidth = nextWidth
      visible.push(folders[i])
    }
    shouldShowEllipsis.value = i < folders.length
    visibleFolders.value = visible
  })
}

const debouncedCalculateVisibleFolders =
  debounce(calculateVisibleFolders, CALCULATE_VISIBLE_FOLDERS_DEBOUNCE_MS)

let resizeObserver: ResizeObserver

watch(() => props.file.fullPath, debouncedCalculateVisibleFolders)
onMounted(() => {
  if (container.value) {
    resizeObserver = new ResizeObserver(() => {
      debouncedCalculateVisibleFolders()
    })
    resizeObserver.observe(container.value)
  }
  debouncedCalculateVisibleFolders()
})
onBeforeUnmount(() => {
  if (resizeObserver && container.value) {
    resizeObserver.unobserve(container.value)
    resizeObserver.disconnect()
  }
  debouncedCalculateVisibleFolders.cancel?.()
})
</script>

<style lang="scss" scoped>
.file-path-part:hover {
  text-decoration: underline;
}

.room-name-width-limit {
  max-width: 25%;
}
</style>
